/**
=========================================================
* Material Dashboard 2 React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useState } from "react";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";

// react-router-dom components
import { useLocation, NavLink, useNavigate } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";
import Box from "@mui/material/Box";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 2 React example components
import SidenavCollapse from "examples/Sidenav/SidenavCollapse";

// Custom styles for the Sidenav
import SidenavRoot from "examples/Sidenav/SidenavRoot";

// Material Dashboard 2 React context
import { useMaterialUIController, setMiniSidenav, setTransparentSidenav, setWhiteSidenav } from "context";

import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SubMenu from "./SubMenu";
import SubMenuQuality from "./SubMenuQuality";
import SubMenuScadenziario from "./SubMenuScadenziario";
import SubMenuSecurity from "./SubMenuSecurity";
import apiService from "../../services/api-service";

function Sidenav({ color, brand, brandName, routes, ...rest }) {
	const [controller, dispatch] = useMaterialUIController();
	const navigate = useNavigate();
	const { miniSidenav, transparentSidenav, whiteSidenav, darkMode, sidenavColor } = controller;
	const location = useLocation();
	const collapseName = location.pathname.replace("/", "");
	const [submenuOpen, setSubmenuOpen] = useState({});
	const [isHovered, setIsHovered] = useState({});
	const [notifications, setNotifications] = useState(0);

	let textColor = "white";

	if (transparentSidenav || (whiteSidenav && !darkMode)) {
		textColor = "dark";
	} else if (whiteSidenav && darkMode) {
		textColor = "inherit";
	}

	const closeSidenav = () => setMiniSidenav(dispatch, true);

	function getNotifications() {
		apiService
			.get("/notification/", { count: true }, {})
			.then((response) => {
				//console.log("NOTIFICHE",response["count"])
				setNotifications(response["count"]);
			})
			.catch((error) => console.error("Error fetching notifications:", error));
	}

	useEffect(() => {
		// getNotifications();
		// setInterval(getNotifications, 150000);
	}, []);

	useEffect(() => {
		// A function that sets the mini state of the sidenav.
		function handleMiniSidenav() {
			setMiniSidenav(dispatch, window.innerWidth < 1200);
			setTransparentSidenav(dispatch, window.innerWidth < 1200 ? false : transparentSidenav);
			setWhiteSidenav(dispatch, window.innerWidth < 1200 ? false : whiteSidenav);
		}

		/** 
     The event listener that's calling the handleMiniSidenav function when resizing the window.
    */
		window.addEventListener("resize", handleMiniSidenav);

		// Call the handleMiniSidenav function to set the state with the initial value.
		handleMiniSidenav();

		// Remove event listener on cleanup
		return () => window.removeEventListener("resize", handleMiniSidenav);
	}, [dispatch, location]);

	// Render all the routes from the routes.js (All the visible items on the Sidenav)
	const renderRoutes = routes.map(({ type, name, icon, title, noCollapse, key, href, route, showInSidebar }) => {
		let returnValue;

		if (type === "collapse" && showInSidebar) {
			returnValue = href ? (
				<Link href={href} key={key} target="_blank" rel="noreferrer" sx={{ textDecoration: "none" }}>
					<SidenavCollapse name={name} icon={icon} active={key === collapseName} noCollapse={noCollapse} />
				</Link>
			) : (
				<NavLink key={key} to={route}>
					<SidenavCollapse name={name} icon={icon} active={key === collapseName} />
				</NavLink>
			);
		} else if (type === "title" && showInSidebar) {
			returnValue = (
				<MDTypography
					key={key}
					color={textColor}
					display="block"
					variant="caption"
					fontWeight="bold"
					textTransform="uppercase"
					sx={{
						textWrap: "wrap",
					}}
					pl={3}
					mt={2}
					mb={1}
					ml={1}
				>
					{title}
				</MDTypography>
			);
		} else if (type === "divider" && showInSidebar) {
			returnValue = (
				<Divider
					key={key}
					light={(!darkMode && !whiteSidenav && !transparentSidenav) || (darkMode && !transparentSidenav && whiteSidenav)}
				/>
			);
		}
		return returnValue;
	});

	return (
		<SidenavRoot
			{...rest}
			variant="permanent"
			ownerState={{
				transparentSidenav,
				whiteSidenav,
				miniSidenav,
				darkMode,
			}}
		>
			<MDBox pt={3} pb={1} px={4} textAlign="center">
				<MDBox
					display={{ xs: "block", xl: "none" }}
					position="absolute"
					top={0}
					right={0}
					p={1.625}
					onClick={closeSidenav}
					sx={{ cursor: "pointer" }}
				>
					<MDTypography variant="h6" color="secondary">
						<Icon sx={{ fontWeight: "bold" }}>close</Icon>
					</MDTypography>
				</MDBox>
				<MDBox
					component={NavLink}
					to="/"
					display="flex"
					flexDirection="column"
					alignItems="center"
					textAlign="center"
					sx={{ textDecoration: "none", padding: "1rem 0" }}
				>
					{brand && <MDBox component="img" src={brand} alt="Brand" width="8rem" sx={{ height: "auto", mb: 1 }} />}
					<MDTypography
						component="h6"
						variant="caption"
						fontWeight="regular"
						color={textColor}
						sx={{
							fontSize: "0.85rem",
							letterSpacing: "0.5px",
							opacity: 0.8,
						}}
					>
						Powered by <strong>EdilMate</strong>
					</MDTypography>
				</MDBox>
				<MDBox
					color="white"
					shadow="inherit"
					variant="h6"
					component="div"
					onClick={() => navigate("/area-selection")}
					sx={{
						cursor: "pointer",
						backgroundColor: "rgb(255, 255, 255, 0.1)",
						padding: "5px",
						borderRadius: "5px",
						wordWrap: "break-word",
						whiteSpace: "normal",
						textAlign: "center",
					}}
				>
					{localStorage.getItem("areaName") ? localStorage.getItem("areaName").toUpperCase() : "SELEZIONA AREA"}
				</MDBox>
			</MDBox>
			<Divider light={(!darkMode && !whiteSidenav && !transparentSidenav) || (darkMode && !transparentSidenav && whiteSidenav)} />
			<List>
				<SubMenu routes={routes} name={"Anagrafiche"} collapseName={collapseName} />
				<SubMenuScadenziario routes={routes} collapseName={collapseName} />
				<SubMenu routes={routes} name={"Sondaggi"} collapseName={collapseName} />
				<SubMenu routes={routes} name={"Gestione cantiere"} collapseName={collapseName} />
				<SubMenu routes={routes} name={"Esporta"} collapseName={collapseName} />
				<SubMenuSecurity routes={routes} collapseName={collapseName} />
				<SubMenuQuality routes={routes} collapseName={collapseName} />
				<SubMenu routes={routes} name={"Rifiuti"} collapseName={collapseName} />
				{renderRoutes}
				<NavLink key={"notifications"} to={"/notifications"}>
					<SidenavCollapse
						name={
							<div
								style={{
									display: "flex",
									alignItems: "center",
									gap: "8px",
								}}
							>
								<span>Notifiche</span>
								{notifications > 0 && (
									<div
										style={{
											display: "flex",
											justifyContent: "center",
											alignItems: "center",
											borderRadius: "50%",
											background: "red",
											color: "white",
											width: "20px",
											height: "20px",
											fontSize: "12px",
											fontWeight: "bold",
										}}
									>
										{notifications}
									</div>
								)}
							</div>
						}
						icon={<NotificationsNoneIcon fontSize="small"></NotificationsNoneIcon>}
						active={"notifications" === collapseName}
						noCollapse={true}
					/>
				</NavLink>
			</List>
			<center>
				<MDBox p={2} mt="auto">
					<MDButton
						component="a"
						onClick={(e) => {
							localStorage.clear();
							navigate(process.env.REACT_APP_API_URL_BACKEND);
						}}
						// href={process.env.REACT_APP_API_URL_BACKEND}
						target="_blank"
						rel="noreferrer"
						variant="gradient"
						color={sidenavColor}
					>
						LOGOUT
					</MDButton>
				</MDBox>
			</center>
			{/* change password */}
			<center>
				<MDBox p={2} mt="auto">
					<MDButton
						component="a"
						onClick={(e) => {
							navigate("/change-password");
						}}
					>
						Change Password
					</MDButton>
				</MDBox>
			</center>
		</SidenavRoot>
	);
}

// Setting default values for the props of Sidenav
Sidenav.defaultProps = {
	color: "info",
	brand: "",
};

// Typechecking props for the Sidenav
Sidenav.propTypes = {
	color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
	brand: PropTypes.string,
	brandName: PropTypes.string.isRequired,
	routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
